import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Calculator, TrendingUp, Shield, Target, DollarSign, Percent, Calendar, RefreshCw } from "lucide-react";
import { API } from "../App";

const Calculators = () => {
  const [activeCalc, setActiveCalc] = useState("roi");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // ROI Calculator state
  const [roiInputs, setRoiInputs] = useState({
    initial_investment: "",
    final_value: ""
  });
  
  // Compound Interest state
  const [compoundInputs, setCompoundInputs] = useState({
    principal: "",
    annual_rate: "",
    years: "",
    compounds_per_year: "12"
  });
  
  // Position Size state
  const [positionInputs, setPositionInputs] = useState({
    account_balance: "",
    risk_percentage: "1",
    entry_price: "",
    stop_loss_price: ""
  });
  
  // Risk/Reward state
  const [riskRewardInputs, setRiskRewardInputs] = useState({
    entry_price: "",
    stop_loss: "",
    take_profit: ""
  });
  
  const calculators = [
    { id: "roi", label: "ROI Calculator", icon: <TrendingUp size={20} /> },
    { id: "compound", label: "Compound Interest", icon: <Percent size={20} /> },
    { id: "position_size", label: "Position Size", icon: <Target size={20} /> },
    { id: "risk_reward", label: "Risk/Reward", icon: <Shield size={20} /> }
  ];
  
  const calculate = async () => {
    setLoading(true);
    setResult(null);
    
    let params = {};
    switch (activeCalc) {
      case "roi":
        params = {
          initial_investment: parseFloat(roiInputs.initial_investment) || 0,
          final_value: parseFloat(roiInputs.final_value) || 0
        };
        break;
      case "compound":
        params = {
          principal: parseFloat(compoundInputs.principal) || 0,
          annual_rate: parseFloat(compoundInputs.annual_rate) || 0,
          years: parseFloat(compoundInputs.years) || 1,
          compounds_per_year: parseInt(compoundInputs.compounds_per_year) || 12
        };
        break;
      case "position_size":
        params = {
          account_balance: parseFloat(positionInputs.account_balance) || 0,
          risk_percentage: parseFloat(positionInputs.risk_percentage) || 1,
          entry_price: parseFloat(positionInputs.entry_price) || 0,
          stop_loss_price: parseFloat(positionInputs.stop_loss_price) || 0
        };
        break;
      case "risk_reward":
        params = {
          entry_price: parseFloat(riskRewardInputs.entry_price) || 0,
          stop_loss: parseFloat(riskRewardInputs.stop_loss) || 0,
          take_profit: parseFloat(riskRewardInputs.take_profit) || 0
        };
        break;
      default:
        break;
    }
    
    try {
      const response = await axios.post(`${API}/calculator`, {
        calculator_type: activeCalc,
        params
      });
      setResult(response.data);
    } catch (error) {
      setResult({ error: error.response?.data?.detail || "Calculation failed" });
    } finally {
      setLoading(false);
    }
  };
  
  const reset = () => {
    setResult(null);
    setRoiInputs({ initial_investment: "", final_value: "" });
    setCompoundInputs({ principal: "", annual_rate: "", years: "", compounds_per_year: "12" });
    setPositionInputs({ account_balance: "", risk_percentage: "1", entry_price: "", stop_loss_price: "" });
    setRiskRewardInputs({ entry_price: "", stop_loss: "", take_profit: "" });
  };
  
  const renderInputs = () => {
    switch (activeCalc) {
      case "roi":
        return (
          <div className="calc-inputs">
            <div className="input-group">
              <label>Initial Investment ($)</label>
              <div className="input-with-icon">
                <DollarSign size={18} />
                <input
                  type="number"
                  value={roiInputs.initial_investment}
                  onChange={(e) => setRoiInputs({ ...roiInputs, initial_investment: e.target.value })}
                  placeholder="10000"
                  data-testid="roi-initial-input"
                />
              </div>
            </div>
            <div className="input-group">
              <label>Final Value ($)</label>
              <div className="input-with-icon">
                <DollarSign size={18} />
                <input
                  type="number"
                  value={roiInputs.final_value}
                  onChange={(e) => setRoiInputs({ ...roiInputs, final_value: e.target.value })}
                  placeholder="15000"
                  data-testid="roi-final-input"
                />
              </div>
            </div>
          </div>
        );
        
      case "compound":
        return (
          <div className="calc-inputs">
            <div className="input-group">
              <label>Principal Amount ($)</label>
              <div className="input-with-icon">
                <DollarSign size={18} />
                <input
                  type="number"
                  value={compoundInputs.principal}
                  onChange={(e) => setCompoundInputs({ ...compoundInputs, principal: e.target.value })}
                  placeholder="10000"
                  data-testid="compound-principal-input"
                />
              </div>
            </div>
            <div className="input-group">
              <label>Annual Interest Rate (%)</label>
              <div className="input-with-icon">
                <Percent size={18} />
                <input
                  type="number"
                  value={compoundInputs.annual_rate}
                  onChange={(e) => setCompoundInputs({ ...compoundInputs, annual_rate: e.target.value })}
                  placeholder="8"
                  data-testid="compound-rate-input"
                />
              </div>
            </div>
            <div className="input-row">
              <div className="input-group">
                <label>Years</label>
                <div className="input-with-icon">
                  <Calendar size={18} />
                  <input
                    type="number"
                    value={compoundInputs.years}
                    onChange={(e) => setCompoundInputs({ ...compoundInputs, years: e.target.value })}
                    placeholder="10"
                    data-testid="compound-years-input"
                  />
                </div>
              </div>
              <div className="input-group">
                <label>Compounds/Year</label>
                <select
                  value={compoundInputs.compounds_per_year}
                  onChange={(e) => setCompoundInputs({ ...compoundInputs, compounds_per_year: e.target.value })}
                  data-testid="compound-frequency-select"
                >
                  <option value="1">Annually</option>
                  <option value="4">Quarterly</option>
                  <option value="12">Monthly</option>
                  <option value="365">Daily</option>
                </select>
              </div>
            </div>
          </div>
        );
        
      case "position_size":
        return (
          <div className="calc-inputs">
            <div className="input-group">
              <label>Account Balance ($)</label>
              <div className="input-with-icon">
                <DollarSign size={18} />
                <input
                  type="number"
                  value={positionInputs.account_balance}
                  onChange={(e) => setPositionInputs({ ...positionInputs, account_balance: e.target.value })}
                  placeholder="10000"
                  data-testid="position-balance-input"
                />
              </div>
            </div>
            <div className="input-group">
              <label>Risk Per Trade (%)</label>
              <div className="input-with-icon">
                <Percent size={18} />
                <input
                  type="number"
                  value={positionInputs.risk_percentage}
                  onChange={(e) => setPositionInputs({ ...positionInputs, risk_percentage: e.target.value })}
                  placeholder="1"
                  data-testid="position-risk-input"
                />
              </div>
            </div>
            <div className="input-row">
              <div className="input-group">
                <label>Entry Price ($)</label>
                <input
                  type="number"
                  value={positionInputs.entry_price}
                  onChange={(e) => setPositionInputs({ ...positionInputs, entry_price: e.target.value })}
                  placeholder="50.00"
                  data-testid="position-entry-input"
                />
              </div>
              <div className="input-group">
                <label>Stop Loss ($)</label>
                <input
                  type="number"
                  value={positionInputs.stop_loss_price}
                  onChange={(e) => setPositionInputs({ ...positionInputs, stop_loss_price: e.target.value })}
                  placeholder="48.00"
                  data-testid="position-stoploss-input"
                />
              </div>
            </div>
          </div>
        );
        
      case "risk_reward":
        return (
          <div className="calc-inputs">
            <div className="input-group">
              <label>Entry Price ($)</label>
              <div className="input-with-icon">
                <DollarSign size={18} />
                <input
                  type="number"
                  value={riskRewardInputs.entry_price}
                  onChange={(e) => setRiskRewardInputs({ ...riskRewardInputs, entry_price: e.target.value })}
                  placeholder="100"
                  data-testid="rr-entry-input"
                />
              </div>
            </div>
            <div className="input-row">
              <div className="input-group">
                <label>Stop Loss ($)</label>
                <input
                  type="number"
                  value={riskRewardInputs.stop_loss}
                  onChange={(e) => setRiskRewardInputs({ ...riskRewardInputs, stop_loss: e.target.value })}
                  placeholder="95"
                  data-testid="rr-stoploss-input"
                />
              </div>
              <div className="input-group">
                <label>Take Profit ($)</label>
                <input
                  type="number"
                  value={riskRewardInputs.take_profit}
                  onChange={(e) => setRiskRewardInputs({ ...riskRewardInputs, take_profit: e.target.value })}
                  placeholder="115"
                  data-testid="rr-takeprofit-input"
                />
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  const renderResult = () => {
    if (!result) return null;
    
    if (result.error) {
      return (
        <div className="result-error">
          <p>{result.error}</p>
        </div>
      );
    }
    
    switch (result.type) {
      case "roi":
        return (
          <motion.div 
            className="result-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h3>ROI Results</h3>
            <div className="result-grid">
              <div className="result-item">
                <span className="label">Initial Investment</span>
                <span className="value">${result.initial_investment?.toLocaleString()}</span>
              </div>
              <div className="result-item">
                <span className="label">Final Value</span>
                <span className="value">${result.final_value?.toLocaleString()}</span>
              </div>
              <div className="result-item highlight">
                <span className="label">Return on Investment</span>
                <span className={`value ${result.roi_percentage >= 0 ? 'positive' : 'negative'}`}>
                  {result.roi_percentage >= 0 ? '+' : ''}{result.roi_percentage}%
                </span>
              </div>
              <div className="result-item">
                <span className="label">Profit/Loss</span>
                <span className={`value ${result.profit_loss >= 0 ? 'positive' : 'negative'}`}>
                  {result.profit_loss >= 0 ? '+' : ''}${result.profit_loss?.toLocaleString()}
                </span>
              </div>
            </div>
          </motion.div>
        );
        
      case "compound":
        return (
          <motion.div 
            className="result-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h3>Compound Interest Results</h3>
            <div className="result-grid">
              <div className="result-item">
                <span className="label">Principal</span>
                <span className="value">${result.principal?.toLocaleString()}</span>
              </div>
              <div className="result-item">
                <span className="label">Interest Rate</span>
                <span className="value">{result.annual_rate}% / year</span>
              </div>
              <div className="result-item">
                <span className="label">Time Period</span>
                <span className="value">{result.years} years</span>
              </div>
              <div className="result-item highlight">
                <span className="label">Final Amount</span>
                <span className="value positive">${result.final_amount?.toLocaleString()}</span>
              </div>
              <div className="result-item">
                <span className="label">Total Interest Earned</span>
                <span className="value positive">+${result.total_interest?.toLocaleString()}</span>
              </div>
            </div>
          </motion.div>
        );
        
      case "position_size":
        return (
          <motion.div 
            className="result-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h3>Position Size Results</h3>
            <div className="result-grid">
              <div className="result-item">
                <span className="label">Account Balance</span>
                <span className="value">${result.account_balance?.toLocaleString()}</span>
              </div>
              <div className="result-item">
                <span className="label">Risk Percentage</span>
                <span className="value">{result.risk_percentage}%</span>
              </div>
              <div className="result-item">
                <span className="label">Max Risk Amount</span>
                <span className="value">${result.max_risk_amount?.toLocaleString()}</span>
              </div>
              <div className="result-item highlight">
                <span className="label">Recommended Shares</span>
                <span className="value">{result.recommended_shares?.toLocaleString()}</span>
              </div>
              <div className="result-item">
                <span className="label">Position Value</span>
                <span className="value">${result.position_value?.toLocaleString()}</span>
              </div>
            </div>
          </motion.div>
        );
        
      case "risk_reward":
        return (
          <motion.div 
            className="result-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h3>Risk/Reward Analysis</h3>
            <div className="result-grid">
              <div className="result-item">
                <span className="label">Entry Price</span>
                <span className="value">${result.entry_price?.toLocaleString()}</span>
              </div>
              <div className="result-item">
                <span className="label">Risk Amount</span>
                <span className="value negative">${result.risk_amount?.toLocaleString()}</span>
              </div>
              <div className="result-item">
                <span className="label">Reward Amount</span>
                <span className="value positive">${result.reward_amount?.toLocaleString()}</span>
              </div>
              <div className="result-item highlight">
                <span className="label">Risk/Reward Ratio</span>
                <span className="value">{result.risk_reward_ratio}</span>
              </div>
              <div className="result-item full-width">
                <span className="label">Recommendation</span>
                <span className={`value ${result.recommendation?.includes('Good') ? 'positive' : 'warning'}`}>
                  {result.recommendation}
                </span>
              </div>
            </div>
          </motion.div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="calculators-page" data-testid="calculators-page">
      <div className="container">
        {/* Header */}
        <div className="calc-header" data-testid="calc-header">
          <h1>Trading Calculators</h1>
          <p>Essential tools for smart trading decisions</p>
        </div>
        
        {/* Calculator Selection */}
        <div className="calc-selector" data-testid="calc-selector">
          {calculators.map(calc => (
            <button
              key={calc.id}
              className={`calc-btn ${activeCalc === calc.id ? 'active' : ''}`}
              onClick={() => { setActiveCalc(calc.id); setResult(null); }}
              data-testid={`calc-btn-${calc.id}`}
            >
              {calc.icon}
              <span>{calc.label}</span>
            </button>
          ))}
        </div>
        
        {/* Calculator Panel */}
        <div className="calc-panel" data-testid="calc-panel">
          <div className="calc-form">
            <div className="form-header">
              <h2>
                {calculators.find(c => c.id === activeCalc)?.icon}
                {calculators.find(c => c.id === activeCalc)?.label}
              </h2>
              <button className="reset-btn" onClick={reset} data-testid="reset-btn">
                <RefreshCw size={16} />
                Reset
              </button>
            </div>
            
            {renderInputs()}
            
            <button 
              className="btn btn-primary calculate-btn" 
              onClick={calculate}
              disabled={loading}
              data-testid="calculate-btn"
            >
              {loading ? 'Calculating...' : 'Calculate'}
              <Calculator size={18} />
            </button>
          </div>
          
          {/* Results */}
          <div className="calc-results" data-testid="calc-results">
            {result ? (
              renderResult()
            ) : (
              <div className="no-result">
                <Calculator size={48} />
                <p>Enter values and click Calculate to see results</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Calculator Info */}
        <div className="calc-info card" data-testid="calc-info">
          <h3>About {calculators.find(c => c.id === activeCalc)?.label}</h3>
          {activeCalc === "roi" && (
            <p>Return on Investment (ROI) measures the gain or loss generated on an investment relative to the amount invested. It's one of the most important metrics for evaluating trading performance.</p>
          )}
          {activeCalc === "compound" && (
            <p>Compound interest is interest calculated on the initial principal and also on the accumulated interest. It's the foundation of long-term wealth building - "the eighth wonder of the world" according to Einstein.</p>
          )}
          {activeCalc === "position_size" && (
            <p>Position sizing determines how many shares or units to trade based on your risk tolerance. Proper position sizing is crucial for capital preservation and consistent profitability.</p>
          )}
          {activeCalc === "risk_reward" && (
            <p>The risk/reward ratio compares potential profit to potential loss. A ratio of 1:2 or better is generally recommended - meaning you should aim to make at least $2 for every $1 risked.</p>
          )}
        </div>
      </div>
      
      <style>{`
        .calculators-page {
          padding: 40px 0 80px;
        }
        
        .calc-header {
          text-align: center;
          margin-bottom: 40px;
        }
        
        .calc-header h1 {
          font-size: 2.5rem;
          margin-bottom: 8px;
        }
        
        .calc-header p {
          color: var(--text-secondary);
        }
        
        /* Calculator Selector */
        .calc-selector {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 32px;
        }
        
        .calc-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 20px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
          color: var(--text-secondary);
          cursor: pointer;
          transition: border-color 0.2s, color 0.2s, background-color 0.2s;
        }
        
        .calc-btn:hover {
          border-color: var(--border-active);
          color: var(--text-primary);
        }
        
        .calc-btn.active {
          border-color: var(--primary);
          background: rgba(59, 130, 246, 0.05);
          color: var(--primary);
        }
        
        .calc-btn span {
          font-size: 13px;
          font-weight: 500;
        }
        
        /* Calculator Panel */
        .calc-panel {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          margin-bottom: 32px;
        }
        
        .calc-form {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 24px;
        }
        
        .form-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }
        
        .form-header h2 {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 1.25rem;
        }
        
        .reset-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: transparent;
          border: 1px solid var(--border);
          border-radius: 6px;
          color: var(--text-muted);
          font-size: 13px;
          cursor: pointer;
        }
        
        .reset-btn:hover {
          color: var(--text-primary);
          border-color: var(--border-active);
        }
        
        .calc-inputs {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-bottom: 24px;
        }
        
        .input-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .input-group label {
          font-size: 13px;
          color: var(--text-muted);
          font-weight: 500;
        }
        
        .input-with-icon {
          position: relative;
        }
        
        .input-with-icon svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
        }
        
        .input-with-icon input {
          padding-left: 40px;
        }
        
        .calc-inputs input,
        .calc-inputs select {
          width: 100%;
          padding: 12px 16px;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          font-family: var(--font-mono);
          font-size: 16px;
        }
        
        .calc-inputs input:focus,
        .calc-inputs select:focus {
          outline: none;
          border-color: var(--primary);
        }
        
        .calc-inputs select {
          cursor: pointer;
        }
        
        .input-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        
        .calculate-btn {
          width: 100%;
          padding: 16px;
          font-size: 16px;
          border-radius: 8px;
        }
        
        /* Results */
        .calc-results {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .no-result {
          text-align: center;
          color: var(--text-muted);
        }
        
        .no-result svg {
          margin-bottom: 16px;
          opacity: 0.3;
        }
        
        .result-card {
          width: 100%;
        }
        
        .result-card h3 {
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--border);
        }
        
        .result-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        
        .result-item {
          padding: 16px;
          background: var(--bg-subtle);
          border-radius: 8px;
        }
        
        .result-item.highlight {
          grid-column: span 2;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.2);
        }
        
        .result-item.full-width {
          grid-column: span 2;
        }
        
        .result-item .label {
          display: block;
          font-size: 12px;
          color: var(--text-muted);
          margin-bottom: 4px;
        }
        
        .result-item .value {
          font-family: var(--font-mono);
          font-size: 1.25rem;
          font-weight: 600;
        }
        
        .result-item.highlight .value {
          font-size: 1.5rem;
        }
        
        .value.positive { color: var(--success); }
        .value.negative { color: var(--error); }
        .value.warning { color: var(--warning); }
        
        .result-error {
          text-align: center;
          color: var(--error);
          padding: 40px;
        }
        
        /* Info Card */
        .calc-info {
          background: var(--bg-paper);
          border: 1px solid var(--border);
        }
        
        .calc-info h3 {
          margin-bottom: 12px;
        }
        
        .calc-info p {
          color: var(--text-secondary);
          line-height: 1.7;
        }
        
        @media (max-width: 1024px) {
          .calc-selector {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .calc-panel {
            grid-template-columns: 1fr;
          }
        }
        
        @media (max-width: 768px) {
          .calc-selector {
            grid-template-columns: 1fr;
          }
          
          .input-row {
            grid-template-columns: 1fr;
          }
          
          .result-grid {
            grid-template-columns: 1fr;
          }
          
          .result-item.highlight,
          .result-item.full-width {
            grid-column: span 1;
          }
        }
      `}</style>
    </div>
  );
};

export default Calculators;